'use client';

import color from '@haaretz/l-color.macro';
import mq from '@haaretz/l-mq.macro';
import radius from '@haaretz/l-radius.macro';
import space from '@haaretz/l-space.macro';
import typesetter from '@haaretz/l-type.macro';
import NoSSR from '@haaretz/s-no-ssr';
import getDays from '@haaretz/s-time-utils/getDays';
import getHours from '@haaretz/s-time-utils/getHours';
import getMinutes from '@haaretz/s-time-utils/getMinutes';
import getSeconds from '@haaretz/s-time-utils/getSeconds';
import getTwoDigitFormat from '@haaretz/s-time-utils/getTwoDigitFormat';
import * as React from 'react';
import s9 from 'style9';

import type { StyleExtend, InlineStyles } from '@haaretz/s-types';

// `c` is short for `classNames`
const c = s9.create({
  base: {
    display: 'flex',
    columnGap: space(1),
    flexDirection: 'row-reverse',
    alignItems: 'center',
    fontWeight: 700,
    ...typesetter(4),

    ...mq({ from: 'l', value: { ...typesetter(5) } }),
  },
  numberWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: color('tertiary100'),
    borderRadius: radius('medium'),
    paddingTop: space(1),
    paddingBottom: space(1),
    paddingRight: space(2),
    paddingLeft: space(2),
  },
});

export interface PpCounterProps {
  /**
   * CSS declarations to be set as inline `style` on the
   * html element.
   *
   * By setting values of CSS Custom Properties based on
   * props or state in the consuming component (where
   * the value of `inlineStyle` is passed), `inlineStyle`
   * can be used as an API contract for setting dynamic
   * values to styles created with `style9.create()`:
   *
   * @example
   * ```ts
   * import s9 from 'style9';
   * const { styleExtend, } = s9.create({
   *   styleExtend: {
   *     color: 'var(--color-based-on-prop)',
   *   },
   * });
   *
   * function MyPpCounter(props) {
   *   const inlineStyle = {
   *     '--color-based-on-prop': props.color,
   *   },
   *
   *   return (
   *    <PpCounter
   *      styleExtend={[ styleExtend, ]}
   *      inlineStyle={inlineStyle}
   *    />
   *   );
   * }
   * ```
   */
  inlineStyle?: InlineStyles;
  /**
   * An array of `Style`s created by `style9.create()`.
   * WARNING: **_do not_** pass simple CSS-in-JS object.
   * The items in the array must be created with Style9's
   * `create` function.
   * The array can also hold falsy values to assist with
   * conditional inclusion of `Style`s:
   *
   * @example
   * ```ts
   * const { foo, bar, } = s9.create({ foo: { ... }, bar: { ... }, });
   * <PpCounter styleExtend={[ someCondition && foo, bar, ]} />
   * ```
   */
  styleExtend?: StyleExtend;
  /** The ending time of the offer in ms */
  endDate: number;
  /** Modal onToggle, should be opened when counter reaches 0 */
  setIsOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

function CounterNumber({ time, withoutColon = false }: { time: number; withoutColon?: boolean }) {
  const formattedTime = getTwoDigitFormat(time);

  return (
    <>
      <div className={s9(c.numberWrapper)}>{formattedTime[0]}</div>
      <div className={s9(c.numberWrapper)}>{formattedTime[1]}</div>
      {withoutColon ? null : ':'}
    </>
  );
}

export default function PpCounter({
  inlineStyle,
  styleExtend = [],
  endDate,
  setIsOpen,
}: PpCounterProps) {
  const [shouldShowCounter] = React.useState(Date.now() < endDate);
  const [remainingTime, setRemainingTime] = React.useState(endDate - Date.now());

  React.useEffect(() => {
    const interval = setInterval(() => {
      setRemainingTime(prev => {
        if (prev <= 0) {
          setIsOpen && setIsOpen(true);
          clearInterval(interval);
          return prev;
        }

        return prev - 1000;
      });
    }, 1000);
  }, [setIsOpen]);

  if (!shouldShowCounter) return null;

  return (
    <NoSSR>
      <div className={s9(c.base, ...styleExtend)} style={inlineStyle}>
        <CounterNumber time={getDays(remainingTime)} />
        <CounterNumber time={getHours(remainingTime)} />
        <CounterNumber time={getMinutes(remainingTime)} />
        <CounterNumber time={getSeconds(remainingTime)} withoutColon />
      </div>
    </NoSSR>
  );
}
